/**
 * Ember — a small, warm presence (拍板 2026-09-13 晚, the second drawing of the
 * day: the 09-13 morning "small warm one" kept its face grammar and gave up
 * its sticker material).
 *
 * A round, slightly pear-shaped drop lit from inside: no outline at all — the
 * volume comes from a highlight at the upper left, a faint darkening at the
 * rim, a soft amber rim light along the bottom edge (clipped to the body, far
 * from the face) and a warm pool of light on the ground beneath. A flame-shaped
 * leaf tip at the top right is its one silhouette mark. Large round eyes with
 * two highlights, blurred blush, a very small mouth; paws are hidden at rest
 * and appear only on the faces that need them.
 *
 * Twenty faces on one body, in two tiers with different product permissions:
 *
 *   core (9) — what Ember says about the conversation, never about itself:
 *     listening (default) · thinking · curious · warm · glad · concerned ·
 *     steady · comforting · quiet. The conversation thread and the voice call
 *     draw only these.
 *   extended (11) — for Result, Mattered, welcome, notifications, empty states
 *     and the landing toy: touched · grateful · delighted · surprised · proud ·
 *     relieved · sad · playful · laughing · sleepy · shy. Sparks, hearts and
 *     the like live only here.
 *
 * This module is the single drawing, as an inline-SVG string, so the
 * server-rendered call page (collect-routes.js) and the SPA
 * (components/EmberMascot.tsx) paint the same creature; motion and face
 * switching live in the sibling stylesheet (ember-mascot.css), driven by one
 * attribute — `data-face` on the wrapper — so a page changes its mood by
 * setting an attribute, never by re-rendering the drawing.
 *
 * `lit: true` is the voice-call variant: the same drawing with a warmer,
 * more saturated body and a stronger rim light — a lamp on the night stage.
 *
 * Browser-safe: no node imports. The stylesheet reaches the server page
 * through ember-mascot-style.js (node-only) and the SPA through a CSS import.
 *
 * The box stays 200 × 240 so every slot sized for the previous drawing keeps
 * its size; the body sits a little lower and its ground light fills the foot.
 */

/** The faces the conversation and the call may draw: interaction states. */
export const MASCOT_CORE_FACES = Object.freeze([
  'listening', 'thinking', 'curious', 'warm', 'glad', 'concerned', 'steady', 'comforting', 'quiet',
]);

/** The faces with a different permission: never in the thread or on a call. */
export const MASCOT_EXTENDED_FACES = Object.freeze([
  'touched', 'grateful', 'delighted', 'surprised', 'proud', 'relieved', 'sad',
  'playful', 'laughing', 'sleepy', 'shy',
]);

/** Every face the drawing carries, core first, in display order. */
export const MASCOT_FACES = Object.freeze([...MASCOT_CORE_FACES, ...MASCOT_EXTENDED_FACES]);

/** The faces an idle mascot (the landing toy) may drift into on its own; the others are event-only. */
export const MASCOT_RANDOM_POOL = Object.freeze(['curious', 'glad', 'shy']);

/** How long a transient face stays before the mascot returns to its base. */
export const MASCOT_HOLD_MS = Object.freeze({ default: 2600, delighted: 3200 });

/** Idle drift: the gap between two random faces is drawn from this range. */
export const MASCOT_RANDOM_GAP_MS = Object.freeze({ min: 8000, max: 20000 });

export function isMascotFace(value) {
  return MASCOT_FACES.includes(value);
}

export function isMascotCoreFace(value) {
  return MASCOT_CORE_FACES.includes(value);
}

const INK = '#3b2a22';
const BODY = 'M 100 42 C 143 42, 173 86, 173 132 C 173 170, 142 190, 100 190 C 58 190, 27 170, 27 132 C 27 86, 57 42, 100 42 Z';
const TIP = 'M 104 54 C 100 36, 110 16, 132 8 C 141 26, 133 46, 114 57 Z';

function defs(p) {
  return `<defs>
<radialGradient id="${p}-body" cx="0.38" cy="0.3" r="0.82"><stop offset="0" stop-color="#fffaf0"/><stop offset="0.45" stop-color="#ffefd8"/><stop offset="0.8" stop-color="#ffdcb4"/><stop offset="1" stop-color="#ffc990"/></radialGradient>
<radialGradient id="${p}-lit" cx="0.38" cy="0.3" r="0.85"><stop offset="0" stop-color="#fff6e4"/><stop offset="0.4" stop-color="#ffe4bd"/><stop offset="0.75" stop-color="#ffc98a"/><stop offset="1" stop-color="#f7a44f"/></radialGradient>
<radialGradient id="${p}-edge" cx="0.5" cy="0.5" r="0.5"><stop offset="0.72" stop-color="#e8a56a" stop-opacity="0"/><stop offset="1" stop-color="#e39a5c" stop-opacity="0.32"/></radialGradient>
<linearGradient id="${p}-tip" x1="0" y1="1" x2="0.6" y2="0"><stop offset="0" stop-color="#ffe6c4"/><stop offset="0.55" stop-color="#f9c184"/><stop offset="1" stop-color="#f0a252"/></linearGradient>
<clipPath id="${p}-clip"><path d="${BODY}"/></clipPath>
<filter id="${p}-b3" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3"/></filter>
<filter id="${p}-b6" x="-40%" y="-40%" width="180%" height="180%"><feGaussianBlur stdDeviation="6"/></filter>
<filter id="${p}-b12" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="12"/></filter>
</defs>`;
}

// ---- face parts (drawn in the body's own coordinates; `__P__` is the instance id prefix) ----
const eye = (cx, cy, r = 7.6) => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${INK}"/><circle cx="${(cx + r * 0.3).toFixed(1)}" cy="${(cy - r * 0.34).toFixed(1)}" r="${(r * 0.36).toFixed(1)}" fill="#fff"/><circle cx="${(cx - r * 0.34).toFixed(1)}" cy="${(cy + r * 0.3).toFixed(1)}" r="${(r * 0.15).toFixed(1)}" fill="#fff" opacity="0.9"/>`;
const st = (d, w = 2.8, c = INK) => `<path d="${d}" fill="none" stroke="${c}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"/>`;
/** Both eyes open, optionally looking somewhere (dx, dy) and unequal (rl, rr). Blink rides on the group. */
const eyesOpen = (dx = 0, dy = 0, rl = 7.6, rr = 7.6) => `<g class="em-eyes">${eye(78 + dx, 110 + dy, rl)}${eye(122 + dx, 110 + dy, rr)}</g>`;
const arcUp = (cx) => st(`M ${cx - 8} 112 Q ${cx} 102 ${cx + 8} 112`, 3); // ^ closed, happy
const arcDown = (cx) => st(`M ${cx - 8} 107 Q ${cx} 114 ${cx + 8} 107`, 3); // ∪ closed, soft
const shut = (cx, dir) => st(`M ${cx - 6 * dir} 104 L ${cx + 4 * dir} 110 L ${cx - 6 * dir} 116`, 2.8); // > <
const halfLid = (cx) => `<path d="M ${cx - 7.4} 110 A 7.4 7.4 0 0 0 ${cx + 7.4} 110 Z" fill="${INK}"/>${st(`M ${cx - 8} 109 L ${cx + 8} 109`, 2.4)}`;
const brow = (cx, outerY, innerY, side) => st(`M ${cx - 9 * side} ${outerY} Q ${cx} ${(outerY + innerY) / 2 - 2} ${cx + 9 * side} ${innerY}`, 2.3);
const browsSoft = (k = 1) => brow(78, 96, 91 + 2 * (1 - k), 1) + brow(122, 96, 91 + 2 * (1 - k), -1);
const browsHigh = () => st('M 70 93 Q 78 89 86 93', 2.3) + st('M 114 93 Q 122 89 130 93', 2.3);
const browsLevel = () => st('M 70 95 L 85 94.5', 2.2) + st('M 115 94.5 L 130 95', 2.2);
const browsRelax = () => st('M 70 96 Q 78 92.5 86 94', 2.2) + st('M 114 94 Q 122 92.5 130 96', 2.2);
const cheeks = (o = 0.5, rx = 10) => `<g class="em-cheeks" opacity="${o}"><ellipse cx="64" cy="128" rx="${rx}" ry="6.5" fill="#f9a39c" filter="url(#__P__-b3)"/><ellipse cx="136" cy="128" rx="${rx}" ry="6.5" fill="#f9a39c" filter="url(#__P__-b3)"/></g>`;
const MOUTH = {
  small: st('M 95 135 Q 100 140 105 135', 2.7),
  warm: st('M 93 134 Q 100 142 107 134', 2.9),
  wide: st('M 90 133 Q 100 146 110 133', 3),
  open: `<path d="M 90 132 Q 100 151 110 132 Z" fill="${INK}"/><path d="M 95 139 Q 100 146 105 139 Z" fill="#f08a7a"/>`,
  laugh: `<path d="M 87 130 Q 100 155 113 130 Z" fill="${INK}"/><path d="M 93 139 Q 100 150 107 139 Z" fill="#f08a7a"/>`,
  o: (r = 4) => `<ellipse cx="100" cy="137" rx="${r}" ry="${(r * 1.25).toFixed(1)}" fill="${INK}"/>`,
  tiny: `<ellipse cx="100" cy="137" rx="2.6" ry="3" fill="${INK}"/>`,
  flat: st('M 95 137 L 105 137', 2.7),
  soft: st('M 95 137 Q 100 135.5 105 137', 2.7),
  wobble: st('M 93 137 Q 96.5 133.5 100 137 Q 103.5 140.5 107 137', 2.7),
  tongue: st('M 92 133 Q 100 143 108 133', 2.9) + `<path d="M 101 138 Q 106 145 110 138 Z" fill="#f08a7a"/>`,
};
const sparkle = (x, y, s, c = '#f2b64a') => `<path class="em-spark" d="M ${x} ${y - s} L ${x + s * 0.28} ${y - s * 0.28} L ${x + s} ${y} L ${x + s * 0.28} ${y + s * 0.28} L ${x} ${y + s} L ${x - s * 0.28} ${y + s * 0.28} L ${x - s} ${y} L ${x - s * 0.28} ${y - s * 0.28} Z" fill="${c}"/>`;
const dots = () => '<g class="em-spark"><circle cx="146" cy="56" r="2" fill="#f2b64a"/><circle cx="154" cy="46" r="2.6" fill="#f2b64a"/><circle cx="163" cy="36" r="3.2" fill="#f2b64a"/></g>';
const heart = (x, y, s, o = 1) => `<g class="em-heart" transform="translate(${x} ${y}) scale(${s})" opacity="${o}"><path d="M 0 .38 C -.12 .22 -.55 -.05 -.55 -.36 C -.55 -.62 -.34 -.74 -.17 -.74 C -.06 -.74 0 -.64 0 -.55 C 0 -.64 .06 -.74 .17 -.74 C .34 -.74 .55 -.62 .55 -.36 C .55 -.05 .12 .22 0 .38 Z" fill="#f28c86"/></g>`;
const tear = (x, y) => `<g class="em-tear"><path d="M ${x} ${y} C ${x - 4} ${y + 5.5}, ${x - 4} ${y + 10}, ${x} ${y + 12} C ${x + 4} ${y + 10}, ${x + 4} ${y + 5.5}, ${x} ${y} Z" fill="#a6d5ef"/><circle cx="${x - 1.2}" cy="${y + 7.5}" r="1" fill="#fff" opacity="0.9"/></g>`;
const glyph = (t, x, y, s, c = '#c8b39b') => `<text x="${x}" y="${y}" font-size="${s}" font-weight="700" font-family="-apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" fill="${c}" text-anchor="middle">${t}</text>`;
const puff = (x, y) => st(`M ${x} ${y} q 4 -4 8 0 q 4 4 8 0`, 2.2, '#c8b39b');

// Paws: soft cream ellipses with no outline, hidden at rest.
const paw = (cx, cy, rot) => `<ellipse cx="${cx}" cy="${cy}" rx="10" ry="7" transform="rotate(${rot} ${cx} ${cy})" fill="#ffe7cf"/><ellipse cx="${cx}" cy="${cy + 1.5}" rx="8" ry="4.5" transform="rotate(${rot} ${cx} ${cy})" fill="#f5c9a3" opacity="0.55"/>`;
const PAWS = {
  low: paw(44, 164, -10) + paw(156, 164, 10),
  up: paw(40, 104, -30) + paw(160, 104, 30),
  halfup: paw(40, 124, -15) + paw(160, 124, 15),
  cheeks: paw(62, 138, -10) + paw(138, 138, 10),
  together: paw(88, 156, 20) + paw(112, 156, -20),
  fist: paw(46, 100, -40),
};

/** Each face: what changes on the one body. */
const FACE = {
  // ---- core: interaction states ----
  listening: () => cheeks(0.5) + eyesOpen() + MOUTH.small,
  thinking: () => cheeks(0.4) + eyesOpen(3, -3, 7, 7) + MOUTH.o(3) + dots(),
  curious: () => cheeks(0.5) + browsRelax() + eyesOpen(0, 0, 8.2, 7) + MOUTH.o(3.2),
  warm: () => cheeks(0.7) + arcUp(78) + arcUp(122) + MOUTH.warm,
  glad: () => cheeks(0.75) + arcUp(78) + arcUp(122) + MOUTH.wide + sparkle(44, 74, 5) + sparkle(160, 64, 4),
  concerned: () => cheeks(0.35) + browsSoft(0.5) + eyesOpen(0, 0, 7.2, 7.2) + MOUTH.soft,
  steady: () => cheeks(0.3) + browsLevel() + eyesOpen(0, 0, 7.4, 7.4) + MOUTH.flat,
  comforting: () => cheeks(0.65) + arcDown(78) + arcDown(122) + MOUTH.small + PAWS.together + heart(100, 146, 11, 0.98),
  quiet: () => cheeks(0.35) + arcDown(78) + arcDown(122) + MOUTH.small,
  // ---- extended ----
  touched: () => cheeks(0.7) + browsRelax() + `<g class="em-eyes">${eye(78, 110, 8.2)}${eye(122, 110, 8.2)}<circle cx="81" cy="106.5" r="2.2" fill="#fff"/><circle cx="125" cy="106.5" r="2.2" fill="#fff"/></g>` + tear(132, 117) + MOUTH.small + PAWS.together,
  grateful: () => cheeks(0.75) + arcUp(78) + arcUp(122) + MOUTH.small + heart(154, 70, 8, 0.9) + PAWS.together,
  delighted: () => cheeks(0.8) + eyesOpen(0, -1, 8, 8) + MOUTH.open + sparkle(42, 74, 6) + sparkle(162, 62, 4.5) + PAWS.up,
  surprised: () => cheeks(0.5) + browsHigh() + eyesOpen(0, 0, 9, 9) + MOUTH.o(4.6) + sparkle(160, 60, 4) + PAWS.halfup,
  proud: () => cheeks(0.7) + eyesOpen() + MOUTH.wide + sparkle(46, 68, 5) + PAWS.fist,
  relieved: () => cheeks(0.55) + browsRelax() + arcDown(78) + arcDown(122) + MOUTH.tiny + puff(120, 133) + PAWS.low,
  sad: () => cheeks(0.45) + browsSoft(1) + eyesOpen(0, 2, 7.2, 7.2) + tear(70, 118) + MOUTH.wobble + PAWS.together,
  playful: () => cheeks(0.7) + arcUp(78) + `<g class="em-eyes">${eye(122, 110)}</g>` + MOUTH.tongue,
  laughing: () => cheeks(0.85) + shut(78, 1) + shut(122, -1) + MOUTH.laugh + PAWS.halfup,
  sleepy: () => cheeks(0.45) + halfLid(78) + halfLid(122) + MOUTH.tiny + glyph('z', 150, 64, 14) + glyph('z', 163, 50, 18),
  shy: () => cheeks(0.9, 13) + eyesOpen(-2, 3, 7, 7) + MOUTH.small + PAWS.cheeks,
};

/**
 * The creature as one inline SVG string inside its `.ember-mascot` wrapper.
 *
 * @param {object} [options]
 * @param {string} [options.face='listening'] the face to start on
 * @param {string[]} [options.faces]  which faces to draw (default: all, for a
 *   mascot that will change face by `data-face`; a face that never changes —
 *   a chat avatar — passes just its own, so a thread of forty turns does not
 *   carry forty × twenty faces)
 * @param {boolean} [options.lit=false] the voice-call variant: warmer body, stronger rim light
 * @param {string} [options.uid='em']      id prefix — unique per instance on a page, [A-Za-z0-9_-]
 * @param {string} [options.className='']  extra classes on the wrapper
 * @param {string} [options.id='']         wrapper id, for a page that flips `data-face` by hand
 * @param {number} [options.width]         CSS px; height follows the 200:240 box
 * @param {'span'|'svg'} [options.tag='span'] the wrapper: a `<span>` for HTML, or
 *   a nested `<svg>` for a drawing that lives inside another SVG scene (the
 *   tree under the landing page) — then `x`, `y` and `width` are that scene's
 *   units and the classes and `data-face` sit on the nested svg itself
 * @param {number} [options.x=0]           svg tag only: the drawing's left edge in the outer scene
 * @param {number} [options.y=0]           svg tag only: the drawing's top edge in the outer scene
 */
export function mascotSvg({ face = 'listening', faces, lit = false, uid = 'em', className = '', id = '', width, tag = 'span', x = 0, y = 0 } = {}) {
  const f = isMascotFace(face) ? face : 'listening';
  const drawn = Array.isArray(faces) ? MASCOT_FACES.filter((k) => faces.includes(k) || k === f) : MASCOT_FACES;
  const p = `em-${String(uid).replace(/[^A-Za-z0-9_-]/g, '')}`;
  const cls = className ? ` ${className}` : '';
  const idAttr = id ? ` id="${id}"` : '';
  const size = Number.isFinite(width) && width > 0
    ? ` style="width:${width}px;height:${Math.round(width * 1.2)}px"`
    : '';
  const faceGroups = drawn.map((k) => `<g class="em-face em-face-${k}">${FACE[k]().replaceAll('__P__', p)}</g>`).join('');
  const bodyFill = lit ? `url(#${p}-lit)` : `url(#${p}-body)`;
  const rim = lit
    ? `<ellipse cx="100" cy="192" rx="72" ry="26" fill="#ffb35a" opacity="0.85" filter="url(#${p}-b12)"/><ellipse cx="100" cy="150" rx="70" ry="50" fill="#ffb35a" opacity="0.22" filter="url(#${p}-b12)"/>`
    : `<ellipse cx="100" cy="192" rx="72" ry="26" fill="#ffb35a" opacity="0.62" filter="url(#${p}-b12)"/>`;
  const inner = `<g transform="translate(0 14)">
<ellipse cx="100" cy="201" rx="54" ry="8" fill="#c9905e" opacity="0.22" filter="url(#${p}-b6)"/>
<ellipse cx="100" cy="189" rx="66" ry="20" fill="#ffb35a" opacity="0.38" filter="url(#${p}-b12)"/>
<g class="em-body">
<g class="em-wisp"><circle cx="128" cy="16" r="10" fill="#ffd9a0" opacity="0.45" filter="url(#${p}-b6)"/><path d="${TIP}" fill="url(#${p}-tip)"/></g>
<path d="${BODY}" fill="${bodyFill}"/>
<path d="${BODY}" fill="url(#${p}-edge)"/>
<g class="em-inner" clip-path="url(#${p}-clip)">${rim}</g>
<g clip-path="url(#${p}-clip)"><ellipse cx="164" cy="122" rx="12" ry="42" fill="#fff" opacity="0.16" filter="url(#${p}-b6)"/></g>
<ellipse cx="66" cy="80" rx="15" ry="24" transform="rotate(22 66 80)" fill="#fff" opacity="0.62" filter="url(#${p}-b6)"/>
<ellipse cx="58" cy="66" rx="5" ry="8" transform="rotate(22 58 66)" fill="#fff" opacity="0.85" filter="url(#${p}-b3)"/>
${faceGroups}
</g></g>`;
  if (tag === 'svg') {
    const w = Number.isFinite(width) && width > 0 ? width : 200;
    return `<svg class="ember-mascot${cls}"${idAttr} data-face="${f}" x="${x}" y="${y}" width="${w}" height="${(w * 1.2).toFixed(1)}" viewBox="0 0 200 240" overflow="visible" focusable="false" aria-hidden="true">${defs(p)}${inner}</svg>`;
  }
  return `<span class="ember-mascot${cls}"${idAttr} data-face="${f}"${size} aria-hidden="true">`
    + `<svg viewBox="0 0 200 240" focusable="false">${defs(p)}${inner}</svg></span>`;
}
